import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { RootState } from "@store/store";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";

// interface StatusStyle {
//   text: string;
//   bg: string;
// }

type FAQItem = {
  q: string;
  a?: string;
  bullets?: string[];
};
export const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useSelector((state: RootState) => state.profile);

  const isLoggedIn = Boolean(
    profile?.id ?? profile?.email ?? localStorage.getItem("accessToken")
  );
  const faqs: FAQItem[] = [
    {
      q: "How can I register on the NOC Portal?",
      bullets: [
        "Visit the MoRTH NOC Portal",
        "Click Register",
        "Enter Aadhaar number and provide consent",
        "Verify OTP sent to Aadhaar-linked mobile number",
        "Submit the form",
        "Registration confirmation is sent via SMS and Email"
      ]
    },
    {
      q: "I did not receive OTP during registration. What should I do?",
      bullets: [
        "Retry OTP after 60 seconds",
        "Ensure Aadhaar is linked with your mobile number",
        "If the issue persists, contact the Helpdesk"
      ]
    },
    {
      q: "Can I log in using a password?",
      a: "No. Login is supported only through OTP-based authentication."
    },
    {
      q: "I am unable to log in due to invalid or expired OTP.",
      bullets: [
        "OTP is valid for 5 minutes",
        "Request a new OTP if expired",
        "After 3 incorrect attempts, retry after 15 minutes"
      ]
    },
    {
      q: "What types of applications can I submit on the NOC Portal?",
      bullets: [
        "Access Permission (Residential, Commercial, Institutional, Government)",
        "ROW Permission (Utilities, OFC, Pipelines, Metro/Railway Crossing, Temporary Usage)"
      ]
    },
    {
      q: "Can I save my application and submit it later?",
      a: "Yes. Applications are auto-saved as drafts. Drafts are available under Dashboard → Saved Applications and remain valid for 30 days."
    },
    {
      q: "How do I mark the project location on the GIS map?",
      bullets: [
        "Use the interactive GIS map provided in the application form",
        "Select National Highway number and chainage",
        "Mark the exact Access point or ROW alignment"
      ]
    },
    {
      q: "Can I apply for Access and ROW in a single application?",
      a: "No. Access NOC and ROW NOC must be submitted as separate applications."
    },
    {
      q: "What documents are required for Access NOC?",
      bullets: [
        "Site Plan / Layout Drawing",
        "Ownership or Lease Proof",
        "Drainage Plan",
        "Site Photographs",
        "Undertaking (if applicable)"
      ]
    },
    {
      q: "What documents are required for ROW NOC?",
      bullets: [
        "Alignment / Utility Layout",
        "Technical Drawings",
        "Land Details",
        "Safety & Traffic Management Plan",
        "Supporting approvals (if applicable)"
      ]
    },
    {
      q: "Can I submit the application without uploading all documents?",
      a: "No. Submission is blocked until all mandatory documents are uploaded."
    },
    {
      q: "When do I need to make payment?",
      a: "Payment is required after application approval and before license issuance."
    },
    {
      q: "How do I e-Sign the application?",
      bullets: [
        "Click E-Sign",
        "Enter Aadhaar-linked OTP",
        "Digital signature is applied",
        "You are redirected to Bharatkosh for payment"
      ]
    },
    {
      q: "My payment failed. What should I do?",
      bullets: [
        "Check transaction status on the Dashboard",
        "Retry payment if required",
        "Amount will be auto-reconciled if debited"
      ]
    },
    {
      q: "My application was rejected. What should I do?",
      bullets: [
        "View rejection reason on the Dashboard",
        "Submit a fresh application or clarification if allowed",
        "Withdrawal is not permitted after submission"
      ]
    },
    {
      q: "How do I respond to a clarification request?",
      bullets: [
        "Go to Dashboard → Application",
        "Click Respond to Clarification",
        "Upload revised documents and submit"
      ]
    },
    {
      q: "How can I track my application status?",
      a: "Login to the Dashboard to view real-time application status."
    },
    {
      q: "What application statuses can appear?",
      bullets: [
        "Draft",
        "Application Submitted",
        "Visit Scheduled",
        "Clarification Requested",
        "Application Approved",
        "Application Rejected",
        "NOC/ License Issued"
      ]
    },
    {
      q: "Will I receive notifications?",
      a: "Yes. Notifications are sent via SMS, Email, and Dashboard alerts."
    },
    {
      q: "How do I request a Final License?",
      bullets: [
        "Upload completion documents",
        "Submit request from Dashboard",
        "e-Sign and pay applicable fee"
      ]
    },
    {
      q: "Can I apply for Extension of Time (EOT)?",
      a: "Yes. EOT can be requested before license expiry through the Dashboard."
    },
    {
      q: "What if my issue is not resolved?",
      bullets: [
        "Raise a ticket through the Portal Helpdesk",
        "Contact the official Helpline or Support Email"
      ]
    }
  ];

  const [expanded, setExpanded] = useState<string | false>(false);
  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) =>
      setExpanded(isExpanded ? panel : false);

  return (
    <Box className="app-details-wrapper">
      <div className="page-content">
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              py: 2,
            }}
          >
            <Button
              onClick={() => navigate("/application-dashboard")}
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              disableRipple
              disableFocusRipple
              sx={{
                textTransform: "none",
                color: "#212121",
                fontWeight: 700,
                pl: 0,
                fontSize: 16,
                bgcolor: "transparent",
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "none"
                }
              }}
            >
              Back to Dashboard
            </Button>
          </Box>

          <Box
            className="w-full bg-gray-000 rounded-lg p-4"
            sx={{
              bgcolor: "#fff",
              borderRadius: "12px",
              border: "1px solid #EEEBF7"
            }}
          >
            {/* FAQ */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 18,
                color: "#212121",
                mb: 1,
                ml: 1,
                mt: 1
              }}
            >
              FAQ
            </Typography>
            {faqs.map((item, i) => {
              const panelId = `panel-${i}`;
              return (
                <Accordion
                  key={panelId}
                  expanded={expanded === panelId}
                  onChange={handleChange(panelId)}
                  disableGutters
                  square
                  sx={{
                    boxShadow: "none",
                    borderBottom:
                      i !== faqs.length - 1 ? "1px solid #EEF2F7" : "none",
                    "&:before": { display: "none" }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#9CA3AF" }} />}
                    aria-controls={`${panelId}-content`}
                    id={`${panelId}-header`}
                    sx={{
                      px: 2,
                      py: 1.25,
                      "& .MuiAccordionSummary-content": { m: 0 }
                    }}
                  >
                    <Typography
                      sx={{ fontWeight: 500, fontSize: 14, color: "#212121" }}
                    >
                      {item.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                    {item.a && (
                      <Typography
                        sx={{ fontSize: 14, color: "#6B7280", lineHeight: 1.6 }}
                      >
                        {item.a}
                      </Typography>
                    )}
                    {item.bullets && (
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {item.bullets.map((b, idx) => (
                          <li key={idx}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                color: "#6B7280",
                                lineHeight: 1.6
                              }}
                            >
                              {b}
                            </Typography>
                          </li>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
          <div style={{ height: "30px" }} />
        </>
      </div>
    </Box>
  );
};
